import React, { useCallback, useMemo, useState } from 'react';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Avatar } from 'primereact/avatar';
import { Badge } from 'primereact/badge';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { FloatLabel } from 'primereact/floatlabel';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';

import { useAuth } from '../../context/AuthContext';
import { getFallbackAvatar } from '../../static/assets/avatars';

import '../../static/styles/Threads.css';

const formatDate = (value) => {
    if (!value) {
        return '';
    }

    const resolveDate = (input) => {
        if (input instanceof Date) {
            return input;
        }

        if (typeof input === 'string' || typeof input === 'number') {
            return new Date(input);
        }

        if (input && typeof input === 'object') {
            if (input.$date) {
                return new Date(input.$date);
            }

            if (typeof input.seconds === 'number') {
                return new Date(input.seconds * 1000);
            }
        }

        return new Date(input);
    };

    try {
        const parsed = resolveDate(value);
        if (Number.isNaN(parsed.getTime())) {
            return '';
        }

        return parsed.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch (error) {
        return '';
    }
};

const sanitizeAvatarValue = (value) => {
    if (!value) {
        return undefined;
    }

    if (typeof value !== 'string') {
        return value;
    }

    const trimmed = value.trim();
    if (!trimmed || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') {
        return undefined;
    }

    try {
        const url = new URL(trimmed);
        if (url.hostname === 'example.com') {
            return undefined;
        }
    } catch (error) {
        // not a URL, proceed
    }

    return trimmed;
};

const resolveAvatarValue = (value, key) => sanitizeAvatarValue(value) || getFallbackAvatar(key);

export default function Threads({
    discussions,
    loading,
    onCreateDiscussion,
    onAddComment,
    onDeleteDiscussion,
    onDeleteComment,
}) {
    const { user, loading: authLoading, isConfigured, signInWithGoogle, signOut } = useAuth();

    const [searchQuery, setSearchQuery] = useState('');
    const [question, setQuestion] = useState('');
    const [description, setDescription] = useState('');
    const [dialogVisible, setDialogVisible] = useState(false);
    const [creatingDiscussion, setCreatingDiscussion] = useState(false);
    const [createError, setCreateError] = useState('');

    const [commentDrafts, setCommentDrafts] = useState({});
    const [commentLoading, setCommentLoading] = useState({});
    const [commentErrors, setCommentErrors] = useState({});

    const isAuthenticated = Boolean(user);
    const userAvatar = isAuthenticated ? resolveAvatarValue(user?.avatar, user?.id || user?.email) : undefined;

    const userIdentifiers = useMemo(() => {
        if (!user) {
            return [];
        }

        return [user.id, user.providerId, user.email].filter(Boolean);
    }, [user]);

    const isOwnedByUser = useCallback((entry) => {
        if (!entry || !userIdentifiers.length) {
            return false;
        }

        const identifiers = [entry.user_id, entry.provider_id, entry.email].filter(Boolean);
        return identifiers.some((value) => userIdentifiers.includes(value));
    }, [userIdentifiers]);

    const resolvedDiscussions = useMemo(() => {
        if (!Array.isArray(discussions)) {
            return [];
        }

        if (!searchQuery.trim()) {
            return discussions;
        }

        const query = searchQuery.toLowerCase();

        return discussions.filter((discussion) => {
            const haystacks = [
                discussion.author,
                discussion.title,
                discussion.content,
            ];

            const commentMatches = (discussion.comments || []).some((comment) => (
                comment.author?.toLowerCase().includes(query)
                || comment.comment?.toLowerCase().includes(query)
            ));

            return haystacks.some((value) => value?.toLowerCase().includes(query)) || commentMatches;
        });
    }, [discussions, searchQuery]);

    const triggerSignIn = async () => {
        if (!isConfigured) {
            return;
        }

        try {
            await signInWithGoogle();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to sign in with Google', error);

            if (error?.message) {
                window.alert(error.message);
            }
        }
    };

    const triggerSignOut = async () => {
        try {
            await signOut();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to sign out', error);
        }
    };

    const handleAskClick = async () => {
        if (!isConfigured) {
            return;
        }

        if (!isAuthenticated) {
            await triggerSignIn();
            return;
        }

        setDialogVisible(true);
    };

    const handleCreateDiscussion = async () => {
        const title = question.trim();
        const content = description.trim();

        if (!title || !content) {
            setCreateError('Both question and description are required.');
            return;
        }

        setCreateError('');
        setCreatingDiscussion(true);

        try {
            await onCreateDiscussion({ title, content });
            setQuestion('');
            setDescription('');
            setDialogVisible(false);
        } catch (error) {
            setCreateError(error?.message || 'Failed to post the discussion.');
        } finally {
            setCreatingDiscussion(false);
        }
    };

    const handleDialogHide = () => {
        if (creatingDiscussion) {
            return;
        }

        setDialogVisible(false);
        setCreateError('');
        setQuestion('');
        setDescription('');
    };

    const handleCommentDraftChange = (discussionId, value) => {
        setCommentDrafts((prev) => ({
            ...prev,
            [discussionId]: value,
        }));
        setCommentErrors((prev) => ({
            ...prev,
            [discussionId]: '',
        }));
    };

    const handleSubmitComment = async (discussionId) => {
        const content = commentDrafts[discussionId]?.trim();

        if (!content) {
            setCommentErrors((prev) => ({
                ...prev,
                [discussionId]: 'Please enter a comment before posting.',
            }));
            return;
        }

        setCommentLoading((prev) => ({
            ...prev,
            [discussionId]: true,
        }));

        try {
            await onAddComment(discussionId, content);
            setCommentDrafts((prev) => ({
                ...prev,
                [discussionId]: '',
            }));
        } catch (error) {
            setCommentErrors((prev) => ({
                ...prev,
                [discussionId]: error?.message || 'Failed to post comment.',
            }));
        } finally {
            setCommentLoading((prev) => ({
                ...prev,
                [discussionId]: false,
            }));
        }
    };

    const dialogFooter = (
        <div className="flex gap-2 justify-content-end">
            <Button
                label="Discard"
                icon="pi pi-times"
                onClick={handleDialogHide}
                text
                disabled={creatingDiscussion}
            />
            <Button
                label="Post"
                icon="pi pi-check"
                onClick={handleCreateDiscussion}
                loading={creatingDiscussion}
                disabled={!isAuthenticated}
                autoFocus
                rounded
            />
        </div>
    );

    const renderDiscussionContent = () => {
        if (loading) {
            return (
                <div className="flex justify-content-center my-6">
                    <ProgressSpinner style={{ width: '4rem', height: '4rem' }} strokeWidth="5" />
                </div>
            );
        }

        if (!resolvedDiscussions.length) {
            return <p className="text-center">No discussions found.</p>;
        }

        return (
            <Accordion activeIndex={0}>
                {resolvedDiscussions.map((discussion) => {
                    const discussionId = discussion._id || discussion.id;
                    const discussionAvatar = resolveAvatarValue(discussion.avatar, discussionId);
                    const canDeleteDiscussion = isOwnedByUser(discussion);

                    const handleDiscussionDelete = () => {
                        if (!onDeleteDiscussion) {
                            return;
                        }

                        Promise.resolve(onDeleteDiscussion(discussionId)).catch((error) => {
                            // eslint-disable-next-line no-console
                            console.error('Failed to delete discussion:', error);
                        });
                    };

                    const renderComments = () => (
                        (discussion.comments || []).map((comment, index) => {
                            const commentAvatar = resolveAvatarValue(comment.avatar, comment.provider_id || comment.email || index);
                            const canDeleteComment = isOwnedByUser(comment);

                            const handleCommentDelete = () => {
                                if (!onDeleteComment) {
                                    return;
                                }

                                Promise.resolve(onDeleteComment(discussionId, comment.id)).catch((error) => {
                                    // eslint-disable-next-line no-console
                                    console.error('Failed to delete comment:', error);
                                });
                            };

                            return (
                                <div key={comment.id || comment.date || index} className="m-1 lg:m-5">
                                    <span className="comment-header flex align-items-center gap-2 w-full">
                                        <Avatar
                                            image={commentAvatar}
                                            icon={!commentAvatar ? 'pi pi-user' : undefined}
                                            label={!commentAvatar ? comment.author?.[0] : undefined}
                                            shape="circle"
                                        />
                                        <span className="font-bold white-space-nowrap">{comment.author}</span>
                                        <span className="comment-date font-normal">{formatDate(comment.date)}</span>
                                        {canDeleteComment && (
                                            <Button
                                                icon="pi pi-trash"
                                                severity="danger"
                                                text
                                                rounded
                                                className="ml-auto"
                                                onClick={handleCommentDelete}
                                                aria-label="Delete comment"
                                            />
                                        )}
                                    </span>
                                    <p>{comment.comment}</p>
                                </div>
                            );
                        })
                    );

                    return (
                        <AccordionTab
                            key={discussionId}
                            header={(
                                <>
                                    <span className="discussion-header flex align-items-center gap-2 w-full">
                                        <Avatar
                                            image={discussionAvatar}
                                            icon={!discussionAvatar ? 'pi pi-user' : undefined}
                                            label={!discussionAvatar ? discussion.author?.[0] : undefined}
                                            shape="circle"
                                        />
                                        <span className="font-bold white-space-nowrap">{discussion.author}</span>
                                        <span className="comment-date font-normal gray">{formatDate(discussion.date)}</span>
                                        <div className="ml-auto flex align-items-center gap-2">
                                            <Badge value={discussion.comments?.length || 0} />
                                            {canDeleteDiscussion && (
                                                <Button
                                                    icon="pi pi-trash"
                                                    severity="danger"
                                                    text
                                                    rounded
                                                    onClick={handleDiscussionDelete}
                                                    aria-label="Delete discussion"
                                                />
                                            )}
                                        </div>
                                    </span>
                                    <h3>{discussion.title}</h3>
                                    <p className="font-normal">{discussion.content}</p>
                                </>
                            )}
                        >
                            {renderComments()}
                            <div className="m-1 lg:m-5">
                                {isAuthenticated ? (
                                    <>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-reply" />
                                            <InputText
                                                value={commentDrafts[discussionId] || ''}
                                                onChange={(e) => handleCommentDraftChange(discussionId, e.target.value)}
                                                placeholder="Add a comment"
                                                disabled={commentLoading[discussionId]}
                                            />
                                        </IconField>
                                        {commentErrors[discussionId] && (
                                            <p className="p-error mt-2">{commentErrors[discussionId]}</p>
                                        )}
                                        <div className="flex justify-content-end mt-2">
                                            <Button
                                                label="Reply"
                                                icon="pi pi-send"
                                                onClick={() => handleSubmitComment(discussionId)}
                                                loading={commentLoading[discussionId]}
                                                disabled={commentLoading[discussionId]}
                                                rounded
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <Divider />
                                        <div className="flex flex-column gap-2">
                                            <p className="font-bold">Sign in to join the discussion.</p>
                                            <div>
                                                <Button
                                                    label="Sign in with Google"
                                                    icon="pi pi-google"
                                                    onClick={triggerSignIn}
                                                    loading={authLoading}
                                                    disabled={!isConfigured || authLoading}
                                                    size='small'
                                                    rounded
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </AccordionTab>
                    );
                })}
            </Accordion>
        );
    };

    return (
        <div className="threads">
            <div className="flex flex-column gap-3">
                <IconField className="w-full" iconPosition="left">
                    <InputIcon className="pi pi-search" />
                    <InputText
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search by title, topics, names, etc."
                        disabled={loading}
                    />
                </IconField>
                <div className="flex flex-column lg:flex-row align-items-center gap-2 justify-content-between">
                    <Button
                        label="Ask a question"
                        icon="pi pi-plus"
                        onClick={handleAskClick}
                        rounded
                        disabled={!isConfigured || authLoading}
                        className="w-full lg:w-auto"
                        size='small'
                    />
                    <div className="flex align-items-center gap-2">
                        {isAuthenticated ? (
                            <>
                                <Avatar
                                    image={userAvatar}
                                    icon={!userAvatar ? 'pi pi-user' : undefined}
                                    shape="circle"
                                    size="large"
                                    label={!userAvatar ? user?.name?.[0] : undefined}
                                />
                                <Button
                                    label="Sign out"
                                    icon="pi pi-sign-out"
                                    onClick={triggerSignOut}
                                    outlined
                                    rounded
                                    size='small'
                                />
                            </>
                        ) : (
                            <Button
                                label="Sign in with Google"
                                icon="pi pi-google"
                                onClick={triggerSignIn}
                                loading={authLoading}
                                disabled={!isConfigured || authLoading}
                                outlined
                                rounded
                                size='small'
                            />
                        )}
                    </div>
                </div>
                <Dialog
                    header="Ask a question"
                    visible={dialogVisible}
                    style={{ width: '50vw' }}
                    breakpoints={{ '52rem': '95vw' }}
                    onHide={handleDialogHide}
                    footer={dialogFooter}
                >
                    {!isAuthenticated && (
                        <p className="p-error mb-3">Sign in to post your discussion.</p>
                    )}
                    <FloatLabel className="mt-4">
                        <InputText
                            id="question"
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            disabled={!isAuthenticated || creatingDiscussion}
                        />
                        <label htmlFor="question">Question</label>
                    </FloatLabel>
                    <FloatLabel className="mt-5">
                        <InputTextarea
                            id="context"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={5}
                            cols={30}
                            disabled={!isAuthenticated || creatingDiscussion}
                            style={{maxWidth: '100%'}}
                        />
                        <label htmlFor="context">Description</label>
                    </FloatLabel>
                    {createError && <p className="p-error mt-3">{createError}</p>}
                </Dialog>
            </div>

            {renderDiscussionContent()}
        </div>
    );
}
